import { Address, Language, Lister } from '@smg-real-estate/hg-rets-schema';

import { Roles } from '@/components/Agency/UserFormData/types';

export interface AgencyAgent {
  headAgencyId: string;
  email: string;
  updatedAt: string;
  firstName: string;
  lastName: string;
  avatarUrl: string | null;
  jobTitle: string | null;
  phoneNumber: string | null;
  mobileNumber: string | null;
  logoBackgroundColor: string | null;
  isPublic: boolean;
  sortOrder?: number;
}

export interface AgencyDetails {
  personId?: string;
  customerId?: string;
  name: Lister['name'] | null;
  legalName: Lister['legalName'];
  logoUrl: Lister['logoUrl'];
  country: Address['country'];
  street: Address['street'];
  locality: Address['locality'];
  postalCode: Address['postalCode'];
  role: Roles;
  mandateAcquisitionActive?: boolean;
  isPremiumAgency?: boolean;
}

export interface AgencyAddress {
  country: string | null;
  state: string | null;
  city: string | null;
  street: string | null;
  postalCode: string | null;
}

export enum SalesforceHgInventoryFocusGroupEnum {
  Rent = 'RENT',
  Sale = 'SALE',
  MixedFocus = 'MIXED FOCUS',
}

export interface AgencyProfileCustomAddress {
  street?: string;
  postalCode: string;
  city: string;
}
export interface AgencyProfile {
  customerId: string;
  immoscoutId?: string;
  customerName: string;
  address: AgencyAddress;
  contact: AgencyProfileContact;
  logo: string;
  subCustomerIds?: Array<string>;
  /**
   * Determine whether the current agency is premium active or not
   */
  isPremiumAgency: boolean;
  /**
   * Determine whether the current agency is Visibility Plus Light account or not (part of GTM26)
   */
  isVisibilityLight?: boolean;
  heroBanner?: string;
  descriptions?: { [key: string]: string };
  services?: Array<string>;
  specializations?: Array<string>;
  hgInventoryFocusGroup?: SalesforceHgInventoryFocusGroupEnum;
  customAddress?: AgencyProfileCustomAddress;
  customPhoneNumber?: string;
}

export interface AgencyProfileContact {
  phone: string | null;
  website: string | null;
  language: Language;
}

export interface AgencyProfileInformation {
  customerId: string;
  descriptions: { [key: string]: string } | null;
  services: Array<string>;
  specializations: Array<string>;
  logoUrl: string | null;
  updatedAt: string;
}

export interface AgencyData {
  name: Lister['name'] | null;
  legalName: Lister['legalName'];
  logoUrl: Lister['logoUrl'];
  country: Address['country'];
  street: Address['street'];
  locality: Address['locality'];
  postalCode: Address['postalCode'];
  activeProducts: Array<AgencyProduct>;
  rating: string;
  salesRegion: string;
  accountOwner: AccountOwner;
  bcsOwner?: AccountOwner;
}

export interface AccountOwner {
  email: string;
  ownerName: string;
  phone: string;
}

export interface AgencyProduct {
  id: string;
  quantity: number;
  agencyId: string;
  productName: string;
  productCode: AgencyProductCodes | string; // not all product codes are enumerated, just UBM-relevant
  platform: string;
  contractStartDate: Date;
  contractEndDate?: Date;
}

export enum AgencyProductCodes {
  WELCOME = '5000.0000',
  VISIBILITY_LIGHT = '5000.2023',
  NEW_CONSTRUCTION_LIGHT_PRIMARY = '5000.1006',
  NEW_CONSTRUCTION_LIGHT_LEGACY_CODE = '7111.0005',
  EXPERIENCED = '5000.0002', // Bronze
  PROFESSIONAL = '5000.0003', // Silver
  EXPERT = '5000.0004', // Gold
  FLATFOX_INTEGRATION = '5000.3000',
  CASALEAD_DE = '5000.3100',
  CASALEAD_FR = '5000.3101',
  CASALEAD_IT = '5000.3102',
  EXPERT_DE = '5000.3104',
  EXPERT_FR = '5000.3105',
  EXPERT_IT = '5000.3106',
  SME = '5000.0024',
}

export enum ApiCallStatus {
  READY = 'READY',
  IN_PROGRESS = 'IN_PROGRESS',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}
